/**
 * Headless "accept all tracked changes" for a .docx buffer, via @eigenpal/docx-editor-agents'
 * DocxReviewer. It loads a buffer, accepts every tracked change, and re-serializes — WITHOUT mounting
 * the editor.
 *
 * reeve uses this to normalise a document to its current, all-accepted state:
 *  - at UPLOAD, before storing, so what we keep is the clean baseline;
 *  - at LOAD, before handing the buffer to the editor, so the editor always opens with no pending
 *    revisions and the ONLY tracked changes are the ones the user (or reeve's fixes) then make.
 *
 * Because it genuinely accepts + re-paginates (rather than hiding markup with CSS), the result has no
 * gaps and no leftover redline — the reason we prefer this over suppressing the editor's rendering.
 *
 * Lazy + memoized like the editor bundle (see docxEditor.ts): the reviewer pulls the heavy docx parser,
 * so the module loads on demand. Client-only; on the server or on any failure it returns the input
 * buffer unchanged (fail-open — a document that can't be normalised still opens, just with its markup).
 */

interface DocxReviewerLike {
  acceptAll: () => number;
  toBuffer: () => Promise<ArrayBuffer>;
}
interface ReviewerModule {
  DocxReviewer: { fromBuffer: (buffer: ArrayBuffer, author?: string) => Promise<DocxReviewerLike> };
}

let reviewerPromise: Promise<ReviewerModule | null> | null = null;

function loadReviewer(): Promise<ReviewerModule | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!reviewerPromise) {
    reviewerPromise = (async () => {
      try {
        return (await import('@eigenpal/docx-editor-agents')) as unknown as ReviewerModule;
      } catch {
        reviewerPromise = null; // clear so a later call can retry
        return null;
      }
    })();
  }
  return reviewerPromise;
}

/**
 * Accept every tracked change in `buffer` and return the resulting clean .docx buffer. Returns the
 * ORIGINAL buffer unchanged if the reviewer can't load or the document can't be processed, so callers
 * can use the result unconditionally.
 */
export async function acceptAllTrackedChanges(
  buffer: ArrayBuffer,
  author = 'reeve',
): Promise<ArrayBuffer> {
  const mod = await loadReviewer();
  if (!mod) return buffer;
  try {
    const reviewer = await mod.DocxReviewer.fromBuffer(buffer, author);
    reviewer.acceptAll();
    return await reviewer.toBuffer();
  } catch {
    return buffer;
  }
}
